import { Box, Button, Card, CardContent, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import { setAnalyses, setMarketing, setUserConfirmed } from '@store/reducer/cookieReducer';
import type { AppState } from '@store/store';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

const CookieBanner: React.FC = () => {
    const theme = useTheme();
    const dispatch = useDispatch();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).layout;
    const cookies = useSelector((state: AppState) => state.cookies);

    if (cookies.userConfirmed) {
        return null;
    }

    const onAccept = () => {
        dispatch(setUserConfirmed(true));
    };
    const onDecline = () => {
        dispatch(setMarketing(false));
        dispatch(setAnalyses(false));
        dispatch(setUserConfirmed(true));
    };

    return (
        <Box
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: alpha(theme.palette.common.black, 0.5),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                p: 2,
            }}
        >
            <Card
                sx={{
                    maxWidth: 500,
                    width: '100%',
                    bgcolor: 'background.paper',
                    boxShadow:
                        theme.palette.mode === 'dark'
                            ? '0 10px 25px rgba(0, 0, 0, 0.5)'
                            : '0 10px 25px rgba(0, 0, 0, 0.15)',
                }}
            >
                <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ mb: 2 }}>
                        {langStrings.cookieTitle}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6 }}>
                        {langStrings.cookieText.replace('[ACCEPT]', langStrings.accept)}
                    </Typography>

                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={onDecline}
                            sx={{
                                flex: { xs: '1 1 100%', sm: '0 1 auto' },
                            }}
                        >
                            {langStrings.decline}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={onAccept}
                            sx={{
                                '&:hover': {
                                    backgroundColor: 'primary.dark',
                                    color: 'primary.contrastText',
                                },
                            }}
                        >
                            {langStrings.accept}
                        </Button>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'center',
                            alignItems: 'center',
                            borderTop: `1px solid ${theme.palette.divider}`,
                            pt: 2,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            component="a"
                            href={`/${ELinks.IMPRESSUM}`}
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {langStrings.imprint}
                        </Typography>
                        <Typography
                            component="a"
                            href={`/${ELinks.KONTAKT}`}
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {langStrings.contact}
                        </Typography>
                        <Typography
                            component="a"
                            href={`/${ELinks.DATENSCHUTZ}`}
                            variant="caption"
                            sx={{
                                color: 'text.secondary',
                                textDecoration: 'none',
                                '&:hover': {
                                    color: 'primary.main',
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            Datenschutz
                        </Typography>
                    </Box>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CookieBanner;
